import React, { useMemo, useState } from "react";
import { Stack } from "@mui/material";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import SectionWrapper from "../../Widgets/SectionWrapper";
import FocusAreaCard from "./FocusAreaCard";
import { FocusAreaHistory, SetFocusAreaModal } from "@answersnow/pkg-clinical-program";
import type { FocusAreaHistoryItem, SetFocusAreaFormValues } from "@answersnow/pkg-clinical-program";
import useGetClinicalApproach from "hooks/api/client/useGetClinicalApproach";
import useSaveClinicalApproach from "hooks/api/client/useSaveClinicalApproach";
import useGetDischargePlanning from "hooks/api/client/useGetDischargePlanning";
import useSaveDischargePlanning from "hooks/api/client/useSaveDischargePlanning";
import { useGlobalToast } from "components/GlobalToastProvider";
import { getFocusAreaDescription, getFocusAreaLabel } from "./constants";
import { getCustomerDetails } from "selectors";

const formatDate = (value?: string | Date | null) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getDurationLabel = (start: Date, end: Date) => {
  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) return "0 days active";

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} active`;

  const months = Math.round((days / 30) * 10) / 10;
  return `${months} month${months === 1 ? "" : "s"} active`;
};

const TreatmentFocusArea: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;
  const customerDetails = useSelector(getCustomerDetails) as any;
  const clientFirstName = customerDetails?.details?.name || "";
  const [historyOpen, setHistoryOpen] = useState(false);
  const [setFocusAreaOpen, setSetFocusAreaOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSavingFlow, setIsSavingFlow] = useState(false);

  const { showToast } = useGlobalToast();

  const { clinicalApproachRecords, refetchClinicalApproach } = useGetClinicalApproach(
    clientIdNum
  );
  const { dischargePlanning, refetchDischargePlanning } = useGetDischargePlanning(clientIdNum);

  const { mutateAsync: saveClinicalApproachMutationAsync } = useSaveClinicalApproach();
  const { mutateAsync: saveDischargePlanningMutationAsync } = useSaveDischargePlanning();

  const historyItems = useMemo<FocusAreaHistoryItem[]>(() => {
    return clinicalApproachRecords.map((record: any, index: number) => {
      const recordId = record?.an_internal_id;
      const createdAt = record?.created_at ? new Date(record.created_at) : null;
      const previousRecord = index > 0 ? clinicalApproachRecords[index - 1] : null;
      let endDate = null;
      if (record?.end_date) {
        endDate = new Date(record.end_date);
      } else if (previousRecord?.created_at) {
        endDate = new Date(previousRecord.created_at);
      }

      const durationOrEnd =
        index === 0
          ? {
              type: "duration" as const,
              value:
                createdAt && !Number.isNaN(createdAt.getTime())
                  ? getDurationLabel(createdAt, new Date())
                  : "-",
            }
          : {
              type: "end" as const,
              value: formatDate(endDate),
            };

      return {
        recordId: String(recordId ?? `clinical-approach-${index}-${record?.created_at ?? "unknown"}`),
        isCurrent: index === 0,
        label: index === 0 ? "Current Treatment Focus Area" : "Treatment Focus Area",
        title: getFocusAreaLabel(record?.treatment_focus_area),
        description: getFocusAreaDescription(record?.treatment_focus_area),
        established: formatDate(record?.created_at),
        durationOrEnd,
        terminalObjective: record?.terminal_objective || "",
        isGraduation: Boolean(record?.is_graduation_objective),
      };
    });
  }, [clinicalApproachRecords]);

  const currentRecord = clinicalApproachRecords[0];
  const hasCurrentFocusArea =
    typeof currentRecord?.treatment_focus_area === "string"
      ? currentRecord.treatment_focus_area.trim().length > 0
      : Boolean(currentRecord?.treatment_focus_area);
  const currentItem = hasCurrentFocusArea ? historyItems[0] : null;

  const modalInitialValues = useMemo(() => {
    if (!isEditMode || !currentRecord) return undefined;

    return {
      focusArea: currentRecord?.treatment_focus_area || "",
      terminalObjective: currentRecord?.terminal_objective || "",
      isGraduation: Boolean(currentRecord?.is_graduation_objective),
    };
  }, [currentRecord, isEditMode]);

  const openEditFocusAreaModal = () => {
    setIsEditMode(true);
    setSetFocusAreaOpen(true);
  };

  const openNewFocusAreaModal = () => {
    setIsEditMode(false);
    setSetFocusAreaOpen(true);
  };

  const closeFocusAreaModal = () => {
    setSetFocusAreaOpen(false);
    setIsEditMode(false);
  };

  const handleSaveFocusArea = async (values: SetFocusAreaFormValues) => {
    if (!clientIdNum) return;
    if (isEditMode && !currentRecord?.an_internal_id) return;

    const mutationClientId = Number(clientIdNum);
    if (!Number.isFinite(mutationClientId) || mutationClientId <= 0) return;

    const carriedTreatmentModality = currentRecord?.treatment_modality || null;
    const carriedTreatmentModalityRationale = carriedTreatmentModality
      ? currentRecord?.treatment_modality_rationale || null
      : null;
    const dischargePlanningRecordId = Number(dischargePlanning?.an_internal_id);
    const currentGeneralizationPlan =
      typeof dischargePlanning?.generalization_plan === "string"
        ? dischargePlanning.generalization_plan
        : null;

    const payload = {
      treatment_focus_area: values.focusArea || null,
      terminal_objective: values.terminalObjective || null,
      is_graduation_objective: values.isGraduation,
      treatment_modality: carriedTreatmentModality,
      treatment_modality_rationale: carriedTreatmentModalityRationale,
      maintenance_plan: currentRecord?.maintenance_plan || null,
    };

    setIsSavingFlow(true);
    try {
      await saveClinicalApproachMutationAsync({
        clientId: mutationClientId,
        ...(isEditMode ? { recordId: currentRecord.an_internal_id } : {}),
        payload,
      });

      if (Number.isFinite(dischargePlanningRecordId) && dischargePlanningRecordId > 0) {
        await saveDischargePlanningMutationAsync({
          clientId: mutationClientId,
          recordId: dischargePlanningRecordId,
          payload: {
            generalization_plan: currentGeneralizationPlan,
          },
        });
      }

      await Promise.all([refetchClinicalApproach(), refetchDischargePlanning()]);
      showToast({ message: isEditMode ? "Treatment focus area updated." : "Treatment focus area set." });
      setSetFocusAreaOpen(false);
      setIsEditMode(false);
    } catch {
      // Error toasts are handled by the save hooks.
    } finally {
      setIsSavingFlow(false);
    }
  };

  return (
    <SectionWrapper hideButtons headingBottomSpacing={0}>
      <Stack sx={{ gap: "24px" }}>
        {currentItem ? (
          <FocusAreaCard
            {...currentItem}
            actionsActive
            onHistoryClick={() => setHistoryOpen(true)}
            onEditClick={openEditFocusAreaModal}
            onSetFocusArea={openNewFocusAreaModal}
          />
        ) : (
          <FocusAreaCard
            emptyState
            clientFirstName={clientFirstName}
            isCurrent
            label="Current Treatment Focus Area"
            title="-"
            description={null}
            established="-"
            durationOrEnd={{ type: "duration", value: "-" }}
            terminalObjective=""
            onSetFocusArea={openNewFocusAreaModal}
          />
        )}
      </Stack>

      <FocusAreaHistory open={historyOpen} onClose={() => setHistoryOpen(false)} items={historyItems} />
      <SetFocusAreaModal
        open={setFocusAreaOpen}
        onClose={closeFocusAreaModal}
        onSave={handleSaveFocusArea}
        isSaving={isSavingFlow}
        saveDisabled={isSavingFlow}
        initialValues={modalInitialValues}
        isEditMode={isEditMode}
        clientFirstName={clientFirstName}
      />
    </SectionWrapper>
  );
};

export default TreatmentFocusArea;
